import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, X } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "../ui/sheet";
import { api, img, money } from "../../lib/api";
import { useLang } from "../../context/LangContext";
import { useCart } from "../../context/CartContext";

export const QtyStepper = ({ value, onChange, max = 20, testid }) => (
  <div className="inline-flex items-center border border-sand">
    <button data-testid={`${testid}-dec`} onClick={() => onChange(value - 1)} disabled={value <= 1} className="grid h-9 w-9 place-items-center disabled:opacity-30"><Minus size={13} /></button>
    <span data-testid={`${testid}-value`} className="w-8 text-center text-sm">{value}</span>
    <button data-testid={`${testid}-inc`} onClick={() => onChange(value + 1)} disabled={value >= max} className="grid h-9 w-9 place-items-center disabled:opacity-30"><Plus size={13} /></button>
  </div>
);

export const CartSheet = () => {
  const { t, L, lang } = useLang();
  const { items, open, setOpen, setQty, remove, keyOf } = useCart();
  const [quote, setQuote] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!open || !items.length) {
      setQuote(null);
      return;
    }
    api.post("/cart/quote", { items: items.map(({ product_id, size, color, qty }) => ({ product_id, size, color, qty })) })
      .then((r) => setQuote(r.data)).catch(() => setQuote(null));
  }, [open, items]);

  const stockErr = (i) => quote?.errors?.find((e) => e.product_id === i.product_id && (e.size ?? i.size) === i.size && (e.color ?? i.color) === i.color);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side={lang === "ar" ? "left" : "right"} data-testid="cart-drawer" className="flex w-full flex-col border-sand bg-ivory p-0 sm:max-w-md">
        <SheetTitle className="border-b border-sand px-7 py-6 font-display text-2xl font-normal">{t("cart")}</SheetTitle>
        {!items.length ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-6 p-8">
            <p className="font-display text-xl" data-testid="cart-empty">{t("empty_cart")}</p>
            <button className="btn-line" onClick={() => { setOpen(false); nav("/shop/all"); }}>{t("continue_shopping")}</button>
          </div>
        ) : (
          <>
            <div className="flex-1 space-y-6 overflow-y-auto px-7 py-6">
              {items.map((i) => {
                const k = keyOf(i);
                const err = stockErr(i);
                return (
                  <div key={k} data-testid="cart-item" className="flex gap-4">
                    <img src={img(i.image)} alt="" className="h-28 w-20 object-cover" />
                    <div className="flex flex-1 flex-col">
                      <div className="flex justify-between gap-2">
                        <Link to={`/product/${i.slug}`} onClick={() => setOpen(false)} className="text-sm">{L(i, "name")}</Link>
                        <button data-testid="cart-item-remove" onClick={() => remove(k)} aria-label={t("remove")}><X size={15} strokeWidth={1.3} /></button>
                      </div>
                      <p className="mt-1 text-xs text-sand-dark">{[i.size, i.color].filter(Boolean).join(" / ")}</p>
                      {err && <p className="mt-1 text-xs text-destructive">{err.error === "insufficient_stock" ? t("only_left", { n: err.available }) : t("unavailable")}</p>}
                      <div className="mt-auto flex items-center justify-between pt-3">
                        <QtyStepper value={i.qty} max={i.max || 20} onChange={(q) => setQty(k, q)} testid="cart-qty" />
                        <span className="text-sm">{money(i.price * i.qty)} {t("sar")}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="border-t border-sand px-7 py-6">
              <div className="flex justify-between text-sm"><span>{t("subtotal")}</span><span data-testid="cart-subtotal">{money(quote?.subtotal ?? items.reduce((s, i) => s + i.price * i.qty, 0))} {t("sar")}</span></div>
              <p className="mt-2 text-xs text-sand-dark">{t("vat_included")}</p>
              <button data-testid="cart-checkout-button" disabled={!!quote?.errors?.length} onClick={() => { setOpen(false); nav("/checkout"); }} className="btn-ink mt-5 w-full">
                {t("checkout")}
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
